import { convertMathJax, typesetMath } from "modules/assessment/AssessmentApp"; // Adjust path as needed
import React, { useEffect, useRef, useState } from "react";
import { QuestionRenderer } from "./QuestionRenderer";
import { QuestionPaper } from "./QuestionPaper";
import { ProtectedVisibility } from "shared/security/ProtectedRoute";

// --- Sub-Component: Defined OUTSIDE to prevent re-rendering bugs ---
const AnswerChoiceRenderer = ({ question, t }) => {
    const questionRef = useRef(null);

    useEffect(() => {
        // Ensure typesetMath exists and runs when questions load
        if (typeof typesetMath === 'function' && questionRef.current) {
            typesetMath(questionRef.current);
        }
    }, [question]);
    // 1. Calculate variables
    const correctChoice = question?.answerChoices?.find(
        (choice) => choice.isCorrect
    );

    const hasAnswerChoices = question?.answerChoices &&
        Array.isArray(question.answerChoices) &&
        question.answerChoices.length > 0;

    const isOpenEnded = question?.questionType === "OPEN_ENDED" ||
        question?.questionType === "FILL_IN_THE_BLANK" ||
        question?.questionType === "MATHS_CALCULATION";

    // 2. Render
    return (
        <>
            {/* A. Standard Answer Choices (Multiple Choice) */}
            {hasAnswerChoices && !isOpenEnded && (
                <div ref={questionRef} className="mt-4 space-y-2">
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        {typeof t === 'function' ? t("answerChoices") : "Answer Choices"}:
                    </h5>
                    {question.answerChoices
                        ?.slice()
                        .sort((a, b) => (a.id || 0) - (b.id || 0))
                        .map((choice, choiceIndex) => {
                            const isCorrect = choice.isCorrect === true;
                            return (
                                <div
                                    key={choice.id || choiceIndex}
                                    className={`flex items-center p-3 border-2 rounded-lg ${isCorrect
                                        ? "bg-green-50 dark:bg-green-900/30 border-green-500 dark:border-green-600"
                                        : "bg-gray-50 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600"
                                        }`}
                                >
                                    <span
                                        className={`mr-3 flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${isCorrect
                                            ? "bg-green-500 dark:bg-green-600 text-white"
                                            : "bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                                            }`}
                                    >
                                        {String.fromCharCode(65 + choiceIndex)}
                                    </span>
                                    <span className="text-gray-900 dark:text-gray-100 flex-1">
                                        {typeof convertMathJax === 'function'
                                            ? convertMathJax(choice.choiceText)
                                            : choice.choiceText}
                                    </span>
                                    {isCorrect && (
                                        <span className="ml-2 text-green-600 dark:text-green-400 font-semibold text-sm">
                                            ✓ {typeof t === 'function' ? t("correct") : "Correct"}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                </div>
            )}

            {/* B. Correct Answer Box (For Open Ended / Fill In Blank) */}
            {/* This block was previously floating outside causing syntax errors */}
            {isOpenEnded && correctChoice && (
                <div ref={questionRef} className="mt-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-500 dark:border-green-600 rounded-lg">
                    <h5 className="text-sm font-semibold text-green-800 dark:text-green-200 mb-1">
                        {typeof t === 'function' ? t("correctAnswer") : "Correct Answer"}:
                    </h5>
                    <div className="text-green-900 dark:text-green-100">
                        {typeof convertMathJax === 'function'
                            ? convertMathJax(correctChoice.choiceText)
                            : correctChoice.choiceText}
                    </div>
                </div>
            )}
        </>
    );
};

// --- Content Component ---
const ShowQuestionsContent = ({ selectedEvaluation, questions, handleCloseQuestionsModal, loadingQuestions, t }) => {
    const [isPaperView, setIsPaperView] = useState(false);

    // Read the base URL from env if available, otherwise default to localhost:3001 for the Admin UI
    const baseUrl = process.env.REACT_APP_ADMINUI_BASE_URL || "http://localhost:3001";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 overflow-y-auto p-4">
            <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-0 z-10">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                            {selectedEvaluation?.name || "Evaluation"}
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {questions?.length || 0} Question{questions?.length !== 1 ? "s" : ""}
                        </p>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-800/50 px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700">
                            <span className={`text-sm font-semibold transition-colors ${!isPaperView ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>Default</span>
                            <button
                                type="button"
                                onClick={() => setIsPaperView(!isPaperView)}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${isPaperView ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                                role="switch"
                                aria-checked={isPaperView}
                            >
                                <span
                                    aria-hidden="true"
                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isPaperView ? 'translate-x-5' : 'translate-x-0'
                                        }`}
                                />
                            </button>
                            <span className={`text-sm font-semibold transition-colors ${isPaperView ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>Paper View</span>
                        </div>
                        <button
                            onClick={handleCloseQuestionsModal}
                            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            aria-label="Close"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Questions Content */}
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    {loadingQuestions ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="text-center">
                                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
                                <p className="mt-4 text-gray-600 dark:text-gray-400">Loading questions...</p>
                            </div>
                        </div>
                    ) : (!questions || questions.length === 0) ? (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            <p>No questions available for this evaluation.</p>
                        </div>
                    ) : isPaperView ? (
                        <QuestionPaper questionsData={questions} subjectName={selectedEvaluation?.name} />
                    ) : (
                        <div className="space-y-6">
                            {questions.map((question, index) => (
                                <div
                                    key={question.id || question.code || index}
                                    className="rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-5 bg-white dark:bg-gray-800"
                                >
                                    <div className="flex justify-between items-start mb-3">
                                        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                            {typeof t === 'function' ? t("question") : "Question"} #{index + 1}
                                        </h4>
                                        <ProtectedVisibility requiredRoles={[]} admin={true}>
                                            <a
                                                href={`${baseUrl}/questions-review?questionId=${question.id || question.code}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium underline"
                                            >
                                                Review Question ↗
                                            </a>
                                        </ProtectedVisibility>
                                    </div>
                                    <QuestionRenderer question={question} index={index} />
                                    <AnswerChoiceRenderer question={question} t={t} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// --- Main Component Function ---
export const ShowQuestions = (selectedEvaluation, questions, handleCloseQuestionsModal, loadingQuestions, t) => {
    return (
        <ShowQuestionsContent
            selectedEvaluation={selectedEvaluation}
            questions={questions}
            handleCloseQuestionsModal={handleCloseQuestionsModal}
            loadingQuestions={loadingQuestions}
            t={t}
        />
    );
};